import { Injectable, inject } from '@angular/core';
import { ExpenditureOptions } from '../../../../../core/constants-repository/project-calculation.constants';
import { ProjectModel } from '../../../../../core/models/project/project.model';
import { ProjectMonetaryCalculationService } from './project-monetary-calculation.service';
import { DateTimeHelper } from '../../../../../features/calendar/util/date-time-helper';
import { TimeBlockProjectModel } from '../../../../../core/models/timeblock/time-block.model';
import { DefaultDateFormat } from '../../../../../shared/localization/formats';
import { BarChartData } from '../../../../../shared/data-types/chart-types';
import { Interval } from 'date-fns';
import { TimeBlockWithTask } from '../../../../../shared/data-types/shared-time-block-types';
import { TimeBlockDurationCalculationService } from '../../../../../features/calendar/components/time-block/calculation/duration/time-block-duration-calculation.service';
import { TimeBlockFeeCalculationService } from './time-block-fee-calculation.service';

@Injectable({
  providedIn: 'root',
})
export class ProjectExpenditureChartTransformerService {
  private readonly colorPalette = [
    '#ff6358',
    '#78d237',
    '#2d73f5',
    '#28b4c8',
    '#ffd246',
    '#9c27b0',
    '#ef6c00',
    '#00acc1',
    '#ab47bc',
    '#66bb6a',
  ];

  private readonly projectMonetaryCalculationService = inject(ProjectMonetaryCalculationService);
  private readonly timeBlockDurationCalculationService = inject(
    TimeBlockDurationCalculationService,
  );
  private readonly timeBlockFeeCalculationService = inject(TimeBlockFeeCalculationService);

  public transformFeesToChartData(
    project: ProjectModel,
    timeRange: Interval,
    timeBlocksWithTasks: readonly TimeBlockWithTask[],
  ): { data: BarChartData[] }[] {
    const taskColorMap = new Map<number, string>();
    const tasks = project.tasks ?? [];
    tasks.forEach((task, index) => {
      taskColorMap.set(task.id, this.colorPalette[index % this.colorPalette.length]);
    });

    const timeBlockFees = this.timeBlockFeeCalculationService.timeBlockFeesOfProject(
      project,
      timeBlocksWithTasks,
      true,
      timeRange,
    );

    const barChartMap = new Map<number, BarChartData[]>();

    for (const fee of timeBlockFees) {
      const timeBlock = fee.timeBlockWithTask.timeBlock;
      const taskModel = timeBlock.timeBlockModel as TimeBlockProjectModel;
      const taskId = +taskModel.task.id;

      const dateKey = DateTimeHelper.format(timeBlock.timeBlockModel.start, DefaultDateFormat);
      const date = new Date(dateKey);

      if (!barChartMap.has(taskId)) {
        const start: BarChartData = {
          date: timeRange.start as Date,
          color: '',
          label: '',
          value: 0,
        };
        const end = { ...start, date: timeRange.end as Date };

        barChartMap.set(taskId, [start, end]);
      }

      const barArray = barChartMap.get(taskId);
      const existing = barArray.find((b) => b.date.getDate() === date.getDate());

      const feeValue =
        project.expenditureType === ExpenditureOptions.ExpendituresInHours
          ? this.timeBlockDurationCalculationService.getTimeBlockTimeInSeconds(
              fee.timeBlockWithTask.timeBlock,
            )
          : this.projectMonetaryCalculationService.calculateFeeSum([fee]);

      if (existing) {
        existing.value += feeValue;
      } else {
        const taskName = taskModel.task.name;
        const taskColor = taskColorMap.get(taskId);

        barArray.push({
          date,
          value: feeValue,
          label: taskName,
          color: taskColor ?? '#cccccc',
        });
      }
    }

    return Array.from(barChartMap.values()).map((dataArray) => ({ data: dataArray }));
  }
}
